import SearchIcon from '@mui/icons-material/Search';
import { Chip, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Holder } from '../../data/types';
import { money } from '../../lib/format';
import { HOLDER_TYPE_LABELS } from '../../lib/labels';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { PageHeader, StatusChip } from '../../ui/primitives';

const FILTERS: { key: string; label: string; test: (holder: Holder) => boolean }[] = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'tfn', label: 'TFN not quoted', test: (holder) => holder.tfnStatus === 'NOT_QUOTED' },
  { key: 'kyc', label: 'KYC not verified', test: (holder) => holder.kycStatus !== 'VERIFIED' },
  { key: 'bank', label: 'Bank unverified', test: (holder) => !holder.bank.verified },
  { key: 'foreign', label: 'Non-resident', test: (holder) => holder.residency !== 'AU' },
  { key: 'pep', label: 'PEP', test: (holder) => holder.pep },
  { key: 'login', label: 'Has app login', test: (holder) => Boolean(holder.investorLoginId) },
];

export default function HoldersPage() {
  const scope = useTenantScope();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') ?? 'all';
  const [search, setSearch] = useState('');

  const valueByHolder = useMemo(() => {
    const map = new Map<string, number>();
    for (const holding of scope.holdings)
      map.set(holding.holderId, (map.get(holding.holderId) ?? 0) + holding.value);
    return map;
  }, [scope.holdings]);

  const rows = useMemo(() => {
    const test = FILTERS.find((item) => item.key === filter)?.test ?? (() => true);
    const term = search.trim().toLowerCase();
    return scope.holders.filter(
      (holder) =>
        test(holder) &&
        (!term ||
          holder.name.toLowerCase().includes(term) ||
          holder.accountNumber.toLowerCase().includes(term) ||
          holder.email.includes(term)),
    );
  }, [scope.holders, filter, search]);

  return (
    <>
      <PageHeader
        title="Holders"
        subtitle={`${scope.holders.length} holder accounts. Each is an investment entity in the core platform, extended with registry data.`}
      />
      <Paper variant="outlined">
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name, account number or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack useFlexGap direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {FILTERS.map((item) => (
              <Chip
                key={item.key}
                label={`${item.label} (${scope.holders.filter(item.test).length})`}
                color={filter === item.key ? 'primary' : 'default'}
                variant={filter === item.key ? 'filled' : 'outlined'}
                onClick={() => setParams(item.key === 'all' ? {} : { filter: item.key })}
              />
            ))}
          </Stack>
        </Stack>
        <DataTable
          rows={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/manager/holders/sample/${row.id}`)}
          defaultSort={{ key: 'value', direction: 'desc' }}
          dense
          columns={[
            {
              key: 'name',
              label: 'Holder',
              sortValue: (row) => row.name,
              render: (row) => (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.accountNumber} · {HOLDER_TYPE_LABELS[row.type]} · {row.contactName}
                  </Typography>
                </>
              ),
            },
            {
              key: 'value',
              label: 'Value',
              align: 'right',
              sortValue: (row) => valueByHolder.get(row.id) ?? 0,
              render: (row) => money(valueByHolder.get(row.id) ?? 0),
            },
            {
              key: 'funds',
              label: 'Funds',
              hideBelow: 'md',
              render: (row) =>
                Array.from(
                  new Set(
                    scope.holdings
                      .filter((holding) => holding.holderId === row.id)
                      .map((holding) => scope.fundById.get(holding.fundId)?.code),
                  ),
                ).join(', ') || '—',
            },
            {
              key: 'kyc',
              label: 'KYC',
              sortValue: (row) => row.kycStatus,
              render: (row) => <StatusChip status={row.kycStatus} />,
            },
            {
              key: 'tfn',
              label: 'TFN/ABN',
              hideBelow: 'md',
              sortValue: (row) => row.tfnStatus,
              render: (row) => <StatusChip status={row.tfnStatus} />,
            },
            {
              key: 'res',
              label: 'Residency',
              hideBelow: 'lg',
              sortValue: (row) => row.residency,
              render: (row) => row.residency,
            },
            {
              key: 'login',
              label: 'App',
              hideBelow: 'lg',
              render: (row) => (row.investorLoginId ? 'Yes' : '—'),
            },
          ]}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 2, pb: 2 }}
        >
          Total value of listed holders:{' '}
          {money(sumBy(rows, (row) => valueByHolder.get(row.id) ?? 0))}
        </Typography>
      </Paper>
    </>
  );
}
